import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Droplets, Sun, ShieldCheck, Wrench, ChevronDown } from 'lucide-react';

const GOLD_TYPES = [
  {
    id: 'yellow',
    name: 'Yellow Gold',
    swatch: 'linear-gradient(135deg, #FFE08A 0%, #E5B53A 100%)',
    description: 'The classic, timeless choice. Yellow gold gets its warm hue from being mixed with copper and zinc. The higher the karat, the warmer and richer the yellow tone.',
    bestFor: 'Traditional designs, mangalsutra, bridal sets, daily-wear chains',
    skinTone: 'Flatters warm and olive skin tones beautifully',
  },
  {
    id: 'rose',
    name: 'Rose Gold',
    swatch: 'linear-gradient(135deg, #F4B5A0 0%, #E8A87C 100%)',
    description: 'A romantic, modern blush created by alloying gold with a higher copper content. Its pinkish hue has dominated Gen-Z and millennial wishlists since 2018.',
    bestFor: 'Engagement rings, minimalist stackables, contemporary statement pieces',
    skinTone: 'Universally flattering — works on warm, cool, and neutral undertones',
  },
  {
    id: 'white',
    name: 'White Gold',
    swatch: 'linear-gradient(135deg, #F5F5F0 0%, #D9D9D4 100%)',
    description: 'A cool, silvery-white finish achieved by alloying gold with palladium or nickel and finishing with rhodium plating for extra brilliance. Replate every 2–3 years to maintain shine.',
    bestFor: 'Diamond solitaires, modern minimalism, edgy office wear',
    skinTone: 'Pops on cool and fair skin tones; pairs beautifully with diamonds',
  },
  {
    id: 'platinum',
    name: 'Platinum',
    swatch: 'linear-gradient(135deg, #E8E8E0 0%, #B8B8B0 100%)',
    description: 'A naturally white, hypoallergenic precious metal that is denser and more durable than gold. Platinum jewelry is typically 95% pure (PT950) — far purer than even 22K gold.',
    bestFor: 'Heirloom-quality engagement rings, minimalist bands, sensitive-skin daily wear',
    skinTone: 'Crisp luxury that suits all skin tones, especially fair-cool',
  },
];

const KARAT_GUIDE = [
  {
    karat: '22K',
    purity: '91.6%',
    composition: '22 parts gold + 2 parts alloy',
    color: 'Deep, rich yellow',
    durability: 'Soft — best for traditional pieces, not daily-wear rings',
    bestFor: 'Mangalsutra, bridal jewelry, investment pieces, traditional necklaces',
    badge: 'Most Pure (Indian Standard)',
    badgeColor: 'bg-[#C27054] text-white',
  },
  {
    karat: '18K',
    purity: '75%',
    composition: '18 parts gold + 6 parts alloy',
    color: 'Warm yellow with a balanced hue',
    durability: 'Excellent balance — durable yet luxurious',
    bestFor: 'Engagement rings, daily-wear pendants, modern designs, gemstone settings',
    badge: 'Sweet Spot',
    badgeColor: 'bg-[#2D3B26] text-white',
  },
  {
    karat: '14K',
    purity: '58.3%',
    composition: '14 parts gold + 10 parts alloy',
    color: 'Lighter yellow, slightly muted',
    durability: 'Very durable — resistant to scratches and dents',
    bestFor: 'Stackable rings, everyday chains, active lifestyles, gym-friendly pieces',
    badge: 'Most Durable',
    badgeColor: 'bg-[#475740] text-white',
  },
  {
    karat: '9K',
    purity: '37.5%',
    composition: '9 parts gold + 15 parts alloy',
    color: 'Pale gold, almost champagne',
    durability: 'Hardest of all — least likely to bend',
    bestFor: 'Budget-friendly fashion jewelry, costume pieces, trendy minimalism',
    badge: 'Most Budget-Friendly',
    badgeColor: 'bg-[#7A8A73] text-white',
  },
];

const CARE_STEPS = [
  {
    icon: Sparkles,
    title: 'Last On, First Off',
    description: 'Always put your jewelry on AFTER applying makeup, perfume, hair spray, and lotion. These products contain chemicals that dull gold and stain stones. Take it off first when changing clothes to avoid snags.',
  },
  {
    icon: Droplets,
    title: 'Clean Gently — Once a Month',
    description: 'Soak in lukewarm water with a few drops of mild dish soap for 10 minutes. Use a soft baby toothbrush to gently scrub crevices. Rinse with clean water and pat dry with a lint-free microfiber cloth. Avoid bleach, ammonia, and chlorine — they corrode gold alloys.',
  },
  {
    icon: Sun,
    title: 'Avoid Heat, Sweat & Chlorine',
    description: 'Remove jewelry before swimming pools, hot tubs, saunas, and sun-exposed beach trips. Salt water and chlorine accelerate tarnishing. Sweat from intense workouts can also dull the polish over time.',
  },
  {
    icon: ShieldCheck,
    title: 'Store Each Piece Separately',
    description: 'Pieces rubbing together cause scratches. Use individual soft pouches or a lined jewelry box with separate compartments. For chains, fasten the clasp before storing to prevent tangling. Keep silver in airtight bags with anti-tarnish strips.',
  },
  {
    icon: Wrench,
    title: 'Annual Professional Service',
    description: 'Visit your jeweler once a year for: prong tightening on stones, clasp inspection, ultrasonic deep-clean, and rhodium replating for white gold. Gold is soft — small dings can become big problems if ignored.',
  },
  {
    icon: Sparkles,
    title: 'Travel Smart',
    description: 'Pack jewelry in a hard case with foam inserts. Never check it in luggage. Photograph each piece before traveling for insurance purposes. Avoid wearing valuable items on flights — pressure changes can affect delicate stones.',
  },
];

const Section = ({ children, className = '' }) => (
  <motion.section
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-50px' }}
    transition={{ duration: 0.6 }}
    className={className}
  >
    {children}
  </motion.section>
);

const FAQItem = ({ q, a, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[rgba(45,59,38,0.1)]" data-testid={`faq-${index}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-4 text-left"
      >
        <span className="text-sm md:text-base font-medium text-[#2D3B26] pr-4">{q}</span>
        <ChevronDown size={18} className={`text-[#7A8A73] flex-shrink-0 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <p className="text-sm text-[#475740] leading-relaxed pb-4">{a}</p>}
    </div>
  );
};

const JewelryGuide = () => {
  return (
    <div className="min-h-screen bg-[#FDFBF7] pt-24 md:pt-32 pb-16 md:pb-24" data-testid="jewelry-guide-page">
      {/* Hero */}
      <div className="max-w-4xl mx-auto px-4 md:px-6 text-center mb-12 md:mb-20">
        <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#C27054] mb-3">The NOVA Edit</p>
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif tracking-tight text-[#2D3B26] leading-[1.1] mb-4">
          Jewelry <span className="italic text-[#C27054]">Guide</span>
        </h1>
        <p className="text-base md:text-lg text-[#475740] leading-relaxed max-w-2xl mx-auto">
          Everything you need to know before buying — gold types, karat purity, and the care rituals that keep your pieces looking new for decades.
        </p>
      </div>

      {/* Gold Types */}
      <Section className="max-w-7xl mx-auto px-4 md:px-6 mb-16 md:mb-24" data-testid="gold-types-section">
        <div className="mb-8 md:mb-12">
          <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#7A8A73] mb-2">Chapter 01</p>
          <h2 className="text-3xl md:text-4xl font-serif tracking-tight text-[#2D3B26]">Know Your Metals</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {GOLD_TYPES.map((g, i) => (
            <motion.div
              key={g.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="bg-white rounded-2xl overflow-hidden shadow-[0_4px_20px_rgba(45,59,38,0.06)] hover:shadow-[0_12px_40px_rgba(45,59,38,0.12)] transition-shadow duration-500"
              data-testid={`gold-type-${g.id}`}
            >
              <div className="h-32 md:h-40" style={{ background: g.swatch }} />
              <div className="p-5 md:p-7">
                <h3 className="text-2xl md:text-3xl font-serif text-[#2D3B26] mb-2">{g.name}</h3>
                <p className="text-sm text-[#475740] leading-relaxed mb-4">{g.description}</p>
                <div className="space-y-2 text-xs">
                  <div className="flex gap-2">
                    <span className="text-[#7A8A73] uppercase tracking-wider font-semibold w-20 flex-shrink-0">Best For</span>
                    <span className="text-[#475740]">{g.bestFor}</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-[#7A8A73] uppercase tracking-wider font-semibold w-20 flex-shrink-0">Skin</span>
                    <span className="text-[#475740]">{g.skinTone}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Karat Guide */}
      <Section className="bg-[#E4E8DF] py-12 md:py-20 mb-16 md:mb-24" data-testid="karat-guide-section">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="mb-8 md:mb-12">
            <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#C27054] mb-2">Chapter 02</p>
            <h2 className="text-3xl md:text-4xl font-serif tracking-tight text-[#2D3B26]">The Karat Spectrum</h2>
            <p className="text-sm md:text-base text-[#475740] mt-3 max-w-2xl">
              "Karat" measures gold purity out of 24 parts. The higher the karat, the purer the gold — but also the softer.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {KARAT_GUIDE.map((k, i) => (
              <motion.div
                key={k.karat}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="bg-white rounded-2xl p-5 md:p-6"
                data-testid={`karat-${k.karat}`}
              >
                <div className="flex items-start justify-between mb-3 gap-2">
                  <div>
                    <p className="text-3xl md:text-4xl font-serif text-[#2D3B26]">{k.karat}</p>
                    <p className="text-xs uppercase tracking-widest text-[#7A8A73] font-semibold mt-1">{k.purity} Pure</p>
                  </div>
                  <span className={`text-[9px] uppercase tracking-wider px-2 py-1 rounded-full font-bold ${k.badgeColor}`}>{k.badge}</span>
                </div>
                <p className="text-xs text-[#7A8A73] italic mb-3">{k.composition}</p>
                <div className="space-y-2 text-xs border-t border-[rgba(45,59,38,0.1)] pt-3">
                  <div>
                    <p className="text-[#7A8A73] uppercase tracking-wider font-semibold mb-0.5">Color</p>
                    <p className="text-[#475740]">{k.color}</p>
                  </div>
                  <div>
                    <p className="text-[#7A8A73] uppercase tracking-wider font-semibold mb-0.5">Durability</p>
                    <p className="text-[#475740]">{k.durability}</p>
                  </div>
                  <div>
                    <p className="text-[#7A8A73] uppercase tracking-wider font-semibold mb-0.5">Best For</p>
                    <p className="text-[#475740]">{k.bestFor}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* Gold Care */}
      <Section className="max-w-7xl mx-auto px-4 md:px-6 mb-16 md:mb-24" data-testid="care-guide-section">
        <div className="mb-8 md:mb-12">
          <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#7A8A73] mb-2">Chapter 03</p>
          <h2 className="text-3xl md:text-4xl font-serif tracking-tight text-[#2D3B26]">Caring for Your Gold</h2>
          <p className="text-sm md:text-base text-[#475740] mt-3 max-w-2xl">
            Follow these 6 simple rituals and your jewelry will outlive trends — and possibly outlast you.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {CARE_STEPS.map((step, i) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="bg-white rounded-2xl p-5 md:p-6 border border-[rgba(45,59,38,0.06)] hover:border-[#C27054]/40 transition-colors duration-300"
                data-testid={`care-step-${i + 1}`}
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-[#fff5f0] flex items-center justify-center flex-shrink-0">
                    <Icon size={18} className="text-[#C27054]" />
                  </div>
                  <div className="flex-1">
                    <p className="text-[10px] uppercase tracking-widest font-bold text-[#C27054]">Step {i + 1}</p>
                    <h3 className="text-base md:text-lg font-serif text-[#2D3B26] leading-tight">{step.title}</h3>
                  </div>
                </div>
                <p className="text-sm text-[#475740] leading-relaxed">{step.description}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      {/* FAQ */}
      <Section className="max-w-3xl mx-auto px-4 md:px-6" data-testid="faq-section">
        <div className="mb-8 md:mb-12 text-center">
          <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#7A8A73] mb-2">Quick Answers</p>
          <h2 className="text-3xl md:text-4xl font-serif tracking-tight text-[#2D3B26]">Common Questions</h2>
        </div>
        <div className="bg-white rounded-2xl px-5 md:px-8 py-2">
          <FAQItem
            index={1}
            q="Can I wear gold jewelry while sleeping?"
            a="Pendants and small studs — yes. Rings and chains — no. Constant friction against pillows wears down polish, and prongs can catch on fabric and bend, loosening stones over time."
          />
          <FAQItem
            index={2}
            q="Is 22K gold safe for daily wear?"
            a="22K is on the softer side. It's perfect for occasional-wear bridal pieces and pendants, but for everyday rings and bangles we recommend 18K or 14K — they hold their shape better with daily use."
          />
          <FAQItem
            index={3}
            q="Why does my white gold ring look yellowish?"
            a="The rhodium plating that gives white gold its bright finish wears off in 2–3 years. Bring it to NOVA for a free replating with any service appointment — it'll look brand new."
          />
          <FAQItem
            index={4}
            q="Will rose gold go out of style?"
            a="Rose gold has been a top-3 jewelry choice every year since 2014 and shows no signs of slowing. The pink hue actually deepens slightly over decades — many love this vintage patina."
          />
          <FAQItem
            index={5}
            q="What's the difference between platinum and white gold?"
            a="Platinum is naturally white and 95% pure; white gold is yellow gold dipped in rhodium for white finish. Platinum is denser, hypoallergenic, and never needs replating — but it costs ~30% more for the same piece."
          />
          <FAQItem
            index={6}
            q="How do I tell if my gold is real?"
            a="Look for a hallmark stamp (916 = 22K, 750 = 18K, 585 = 14K, 375 = 9K). NOVA pieces are BIS Hallmark certified. You can also do a magnet test — real gold is non-magnetic."
          />
        </div>
      </Section>
    </div>
  );
};

export default JewelryGuide;
